import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import { DndContext, PointerSensor, useDndMonitor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor, restrictToWindowEdges } from "@dnd-kit/modifiers";
import Thumbnail from "src/components/thumbnail";
import { Nav, Tab } from "react-bootstrap";
import { CSS } from '@dnd-kit/utilities';
import { SoundVisualizer } from "../simulator/visualiser";
import DailyIframe from "@daily-co/daily-js";
import ordinal from "ordinal";
import { format } from "date-fns";
import { Dropdown } from "react-bootstrap";
import { SimulationContext } from "../context";
import { CallRecordingPlayer } from "../simulator/player";

import Actions from "src/components/actionbar";
import Slider from "src/components/slider";
import Button from "src/components/button";
import { buildSimulationLearnerReportWorkbook, SIMULATION_OVERALL_SCHEMA } from "src/pages/coaching/manage/simulations/reports/components/detail/simulationLearnerReportExport";
import DownloadExcel from "src/components/downloadExcel";

import { FactCheckEvaluation, LearningRecordSimulation, SimulationAssessmentReport, SimulationParameterEvaluation } from "src/types/coaching/learners";

import { CALL_TYPES, isSimulationAudioFormat } from "src/constants/simulation";
import { joinDailyRoom } from "src/redux/actions/coaching/learners";
import { fetchLearnerReport } from "src/redux/actions/coaching/simulation";

import { FileService } from "src/utils/file.service";
import dateFormat from "dateformat";
import { getFullName } from "src/utils/utils";

import _ from "lodash";
import cx from "classnames";
import styles from "./dailySimulator.module.scss";

interface DailySimulatorProps {
  simulation: LearningRecordSimulation;
  className?: string;
}

export const DailySimulator: React.FC<DailySimulatorProps> = ({
  simulation,
  className
}) => {
  const dispatch = useDispatch();
  const simulationContext = React.useContext(SimulationContext);
  const allowLearnerAttemptsDropdown = simulationContext.showLearnerAttemptsDropdown !== false;

  const name = useMemo(
    () => _.get(simulation, "simulation.name") || "",
    [simulation]
  );
  const [showEndPitchDialog, setShowEndPitchDialog] = useState(false);
  const [showEndPitchConfirmation, setShowEndPitchConfirmation] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [callAttempt, setCallAttempt] = useState(0);

  const showCall = simulationContext.view === "simulator";
  const showCompleted = simulationContext.view === "completion";
  const showFailed = simulationContext.view === "failed";
  const showAssessmentView = simulationContext.view === 'report';

  const [showAttemptsDropdown, setShowAttemptsDropdown] = useState(false);

  const learnerAttempts = useMemo(() => {
    const attempts = (_.get(simulation, "attempts", []) as any[]) ?? [];
    const report = _.get(simulation, "report");
    const isProcessing =
      !!report &&
      report.status !== "completed" &&
      report.status !== "in_sufficient_duration" &&
      !(report.status === "failed" && report.errorCode === 100002);

    return attempts
      .map((item, index, arr) => ({
        ...item,
        attemptNumber: arr.length - index,
      }))
      .filter((a) => {
        if ((a.score ?? 0) > 0) return true;
        const attemptReport =
          a?.id === simulation.id ? (a?.report ?? report) : a?.report;
        if (attemptReport?.status === "in_sufficient_duration") return false;
        if (attemptReport?.status === "failed") return true;
        return a?.status === "COMPLETED";
      })
      .filter((a) => !isProcessing || (a?.id && a.id !== simulation.id));
  }, [simulation]);

  const selectedAttemptId = (_.get(simulationContext, "selectedAttemptId") as string | undefined) ?? simulation.id;

  const activeAttempt = useMemo(
    () => learnerAttempts.find((a) => a?.id === selectedAttemptId) ?? null,
    [learnerAttempts, selectedAttemptId],
  );

  const selectedAttemptNumber = useMemo(() => {
    if (activeAttempt?.attemptNumber != null) {
      return activeAttempt.attemptNumber;
    }
  
    const attemptsList = (_.get(simulation, "attempts", []) as any[]) ?? [];
    const idx = attemptsList.findIndex((a) => a?.id === selectedAttemptId);
  
    return idx >= 0 ? attemptsList.length - idx : 1;
  }, [activeAttempt?.attemptNumber, selectedAttemptId, simulation]);

  const handleAttemptSelect = (attemptId: string) => {
    setShowAttemptsDropdown(false);
    simulationContext.viewReport?.(attemptId);
  };

  const stopCallHandlerRef = useRef<() => void>();

  const callTransition = useTransition(showCall, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const completeTransition = useTransition(showCompleted, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const sideBarTransition = useTransition(showDetails, {
    from: { opacity: 0, transform: "translateX(100%)" },
    enter: { opacity: 1, transform: "translateX(0%)" },
    leave: { opacity: 0, transform: "translateX(100%)" },
  });

  const failedTransition = useTransition(showFailed, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const assessmentTransition = useTransition(showAssessmentView, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const handleExit = () => {
    if (simulationContext.view === 'report') {
      if (simulationContext.secondaryView === 'call-recording') {
        simulationContext.changeSecondaryView?.(null);
      } else {
        simulationContext.changeView?.("summary");
      }

    } else if (simulationContext.view === 'simulator') {
      setShowEndPitchDialog(true);
    } else if (simulationContext.view === 'completion') {
      simulationContext.changeView?.("summary");
    } else if (simulationContext.view === 'failed') {
      simulationContext.changeView?.("summary");
    }
  }

  const handleEndSimulation = () => {
    setShowEndPitchDialog(true);
  };

  const handleTimeEnd = () => {
    simulationContext.endSimulation?.(simulation);
  };

  const handleRestartSimulation = () => {
    if (simulationContext.restartSimulation) {
      simulationContext.restartSimulation();
    }
  }

  const handleViewCallRecording = () => {
    simulationContext.changeSecondaryView?.("call-recording");
  }

  const handleRestartPitch = () => {
    setShowEndPitchConfirmation(false);
    setCallAttempt(prev => prev + 1);
  };

  const handleAssessmentClicked = () => {
    if (simulationContext.viewReport) {
      simulationContext.viewReport();
    }
  }

  const handleEndSimulationConfirm = () => {
    setShowEndPitchDialog(false);
    stopCallHandlerRef.current?.();
    simulationContext.endSimulation?.(simulation);
  };
  const me = useSelector((state: any) => _.get(state, "users.me"));

  const learnerReportLoading = useSelector((state: any) =>
    _.get(state, "coaching.simulations.learnerReport.loading")
  );
  const learnerReportLoaded = useSelector((state: any) =>
    _.get(state, "coaching.simulations.learnerReport.loaded")
  );
  const learnerReportError = useSelector((state: any) =>
    _.get(state, "coaching.simulations.learnerReport.error")
  );

  const workbook = useMemo(
    () => buildSimulationLearnerReportWorkbook(simulationContext.assessmentReport!),
    [simulationContext.assessmentReport],
  );

  const overallExportRows = useMemo(() => {
    if (!workbook?.data[0]) return [];
    return workbook.data[0];
  }, [workbook]);

  const exportPagination = useMemo(() => {
    const totalRows = workbook
      ? _.sumBy(workbook.data, (sheet) => _.size(sheet))
      : 1;
    return {
      currentPage: 0,
      totalPages: 1,
      recordsPerPage: Math.max(totalRows, 1),
      totalRecordsCount: Math.max(totalRows, 1),
      hasMoreItems: false,
    };
  }, [workbook]);

  const exportFileName = useMemo(() => {
    const simulationName = simulation?.simulation?.name ?? "simulation";
    const learnerPart = getFullName(me)?.trim() || me?.email || "learner";
    const slug = (s: string) =>
      String(s)
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 48) || "x";
    const attemptPart = String(selectedAttemptNumber ?? 1);
    return `${slug(simulationName)}_${slug(learnerPart)}_${attemptPart}`;
  }, [simulation?.simulation?.name, me, selectedAttemptNumber]);

  const exportFetchState = {
    loading: learnerReportLoading,
    succeeded: learnerReportLoaded,
    error: learnerReportError,
  };

  return (
    <div
      className={cx(
        styles.callSimulationContainer,
        {
          [styles.open]: showDetails
        },
        className
      )}
    >
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <img
            className={styles.logo}
            src="/images/coaching/logo.svg"
            alt="Coaching"
          />
          <div className={styles.divider} />
          <div className={styles.name}>{name}</div>

          <div className={styles.actions}>
            {showAssessmentView && simulationContext.secondaryView !== 'call-recording' && (
              <>
                {allowLearnerAttemptsDropdown && (
                  <>
                    <AttemptsDropdown
                      attempts={learnerAttempts}
                      activeAttempt={activeAttempt}
                      selectedAttemptId={selectedAttemptId}
                      showAttemptsDropdown={showAttemptsDropdown}
                      onToggle={setShowAttemptsDropdown}
                      onSelect={handleAttemptSelect}
                    />
                  </>
                )}
                {simulationContext.assessmentReport?.status !== "failed" && (
                  <Button className={styles.viewRecordingAction} variant="secondary" icon="paperflite-waveform" text="view call recording" onClick={handleViewCallRecording} />
                )}
              </>
            )}
            
            {showAssessmentView && simulationContext.assessmentReport?.status !== "in_sufficient_duration" &&
              !(simulationContext.assessmentReport?.status === "failed" &&
                simulationContext.assessmentReport?.errorCode === 100002) &&
              _.size(simulationContext.assessmentReport?.parameters) > 0 && (
                <>
                  <div className={styles.summaryExport}>
                    <DownloadExcel
                      title="Call Assessment Report"
                      fileName={exportFileName}
                      schema={SIMULATION_OVERALL_SCHEMA}
                      data={overallExportRows}
                      multiSheet={Boolean(workbook)}
                      multiSheetData={workbook?.data}
                      multiSheetSchemas={workbook?.schemas as any}
                      multiSheetNames={workbook?.sheets}
                      fetchState={exportFetchState}
                      pagination={exportPagination}
                      fetchDataForPage={() =>
                        dispatch(
                          fetchLearnerReport({
                            id: selectedAttemptId ?? simulation.id!,
                          }),
                        )
                      }
                      invalidateData={() => { }}
                    />
                  </div>
                  <div className={styles.divider} />
                </>
              )}
              
            <button className={styles.action} onClick={handleExit}>
              <i className="paperflite-close" />
            </button>
          </div>
        </div>

        <div className={styles.body}>
          {callTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.stack}>
                  <DailyCallContainer
                    simulation={simulation}
                    key={callAttempt}
                    onDetailsClicked={() => setShowDetails(!showDetails)}
                    onTimeEnd={handleTimeEnd}
                    onEndSimulation={handleEndSimulation}
                    onStop={(stopHandler) => (stopCallHandlerRef.current = stopHandler)}
                  />
                </animated.div>
              )
          )}

          {assessmentTransition(
            (style, item) => {
              if (item) {

                const { transform, ...rest } = style;

                return (
                  <animated.div style={{
                    ...rest,
                    transform: transform.to((t) => (t ? `translateX(${t}%)` : 'none')),
                  }} className={styles.stack}>
                    {simulationContext.assessmentReport &&
                      <CallAssessmentView simulation={simulation} report={simulationContext.assessmentReport} />
                    }
                    {!simulationContext.assessmentReport &&
                      <div className={styles.loading}>Loading assessment report...</div>
                    }
                  </animated.div>
                )
              }
            })}

          {sideBarTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.sideContainer}>
                  {showDetails && <SimulationDetails simulation={simulation} onClose={() => setShowDetails(false)} />}
                </animated.div>
              )
          )}

          {completeTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.stack}>
                  <CompletedCallView simulation={simulation}
                    onFeedback={handleAssessmentClicked}
                    onExit={handleExit}
                  />
                </animated.div>
              )
          )}

          {failedTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.stack}>
                  <FailedCallView simulation={simulation}
                    report={simulationContext.assessmentReport!}
                    onRestart={handleRestartSimulation}
                    onExit={handleExit}
                  />
                </animated.div>
              )
          )}
        </div>
      </div>
      <EndPitchDialog
        show={showEndPitchDialog}
        simulation={simulation}
        onFinish={handleEndSimulationConfirm}
        onClose={() => setShowEndPitchDialog(false)}
      />

      <EndPitchConfirmationDialog
        show={showEndPitchConfirmation}
        simulation={simulation}
        onFinish={() => {
          stopCallHandlerRef.current?.();
          setShowEndPitchConfirmation(false);
          simulationContext.endSimulation?.(simulation);
        }}
        onClose={handleRestartPitch}
      />
    </div>
  );
};

interface AttemptsDropdownProps {
  attempts: any[];
  activeAttempt: any;
  selectedAttemptId: string;
  showAttemptsDropdown: boolean;
  onToggle: (open: boolean) => void;
  onSelect: (id: string) => void;
}

const AttemptsDropdown: React.FC<AttemptsDropdownProps> = ({
  attempts,
  activeAttempt,
  selectedAttemptId,
  showAttemptsDropdown,
  onToggle,
  onSelect,
}) => {
  if (attempts.length <= 1) return null;

  const score = Math.round(((activeAttempt?.score ?? 0) as number) * 100);
  const attemptLabel = ordinal(activeAttempt?.attemptNumber ?? attempts.length);

  return (
    <Dropdown
      className={styles.attemptsDropdown}
      show={showAttemptsDropdown}
      onToggle={onToggle}
    >
      <Dropdown.Toggle as="button" className={styles.attemptsToggle}>
        <div className={styles.attemptsToggleLabels}>
          <span className={styles.attemptsScore}>{score}%</span>
          <span className={styles.attemptsToggleDivider} aria-hidden />
          <span className={styles.attemptsCount}>{attemptLabel} attempt</span>
        </div>
        <i
          className={cx("paperflite-dropdown", styles.attemptsChevron,{
            [styles.openAttempts]: showAttemptsDropdown,
          })}
        />
      </Dropdown.Toggle>
      <Dropdown.Menu
        className={styles.attemptsMenu}
        align="start"
        popperConfig={{
          modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
        }}
      >
        {attempts.map((attempt) => {
          const ts = attempt?.modifiedDate ?? attempt?.createdDate ?? 0;

          const formattedDateTime = format(new Date(ts), "do MMM hh:mm a");
          return (
            <Dropdown.Item
              key={attempt?.id ?? attempt?.attemptNumber}
              className={cx(styles.attemptsItem, {
                [styles.attemptsItemActive]: attempt.id === selectedAttemptId,
              })}
              onClick={() => attempt.id && onSelect(attempt.id)}
            >
              {String(attempt.attemptNumber).padStart(2, "0")} /{" "}
              {Math.round((attempt.score ?? 0) * 100)}% / {formattedDateTime}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const DailyCallContainer: React.FC<{
  simulation: LearningRecordSimulation;
  onDetailsClicked: () => void;
  onEndSimulation: () => void;
  onTimeEnd: () => void;
  onStop: (stopFn: () => void) => void;
}> = ({
  simulation,
  onDetailsClicked,
  onEndSimulation,
  onTimeEnd,
  onStop
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const callObjectRef = useRef<any>(null);
    const localStreamRef = useRef<MediaStream | null>(null);

    const dispatch = useDispatch();

    const user = useSelector((state: any) => _.get(state, 'users.me', {}));
    const userAvatar = _.get(user, 'icon.full', '');

    const roomState = useSelector((state: any) =>
      _.get(state, `learners.simulation.${simulation.id}.room`)
    );

    const [countdown, setCountdown] = useState<number | null>(null);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [showRestartSlider, setShowRestartSlider] = useState(false);
    const [timerReset, setTimerReset] = useState<number>(0);
    const [recordingReady, setRecordingReady] = useState(false);

    const callReady = localStream !== null;
    const started = callReady && countdown === null && !!roomState?.joined && recordingReady;

    const isAudioOnly = isSimulationAudioFormat(simulation.simulation?.format);

    const initials = `${(_.get(user, 'firstName', '')[0] || '')}${(_.get(user, 'lastName', '')[0] || '')}`;

    const updateLocalStream = (stream: MediaStream | null) => {
      localStreamRef.current = stream;
      setLocalStream(stream);
    };

    useEffect(() => {
      if (!callReady || !simulation.session) return;
      dispatch(joinDailyRoom(simulation.session));
    }, [callReady]);

    useEffect(() => {
      if (!roomState?.joined || countdown === null) return;

      if (countdown === 0) {
        const startPitchRecording = async () => {
          try {
            if (!callObjectRef.current) return;
            await callObjectRef.current.startRecording();
            setRecordingReady(true);
            setCountdown(null);
          } catch (err) {
            console.error("Failed to start recording", err);
            setCountdown(null);
          }
        };
        startPitchRecording();
        return;
      }

      const timeout = setTimeout(() => setCountdown(prev => prev! - 1), 1000);
      return () => clearTimeout(timeout);
    }, [roomState?.joined, countdown]);

    useEffect(() => {
      if (!roomState?.joined || !roomState.roomUrl || !roomState.token) return;
      if (!localStreamRef.current) return;
      if (callObjectRef.current) return; 

      const connectToDaily = async () => {
        try {
          const callObject = DailyIframe.createCallObject();
          callObjectRef.current = callObject;

          callObject.on('recording-started', () => {
            setRecordingReady(true);
          });
    
          await callObject.join({
            url: roomState.roomUrl!,
            token: roomState.token!,
            audioSource: localStreamRef.current!.getAudioTracks()[0],
            videoSource: !isAudioOnly ? localStreamRef.current!.getVideoTracks()[0] : undefined,
          });

          setCountdown(3);

          if (videoRef.current) {
            videoRef.current.srcObject = localStreamRef.current;
          }

          onStop(stopCall);
        } catch (err) {
          console.error("Failed to connect to Daily", err);
        }
      };

      connectToDaily();
    }, [roomState?.joined]);

    const getMedia = async () => {
      try {
        const constraints = isAudioOnly ? { audio: true, video: false } : { audio: true, video: true };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        updateLocalStream(stream);
      } catch (err) {
        console.error("Permission denied or failed to get media", err);
      }
    };

    const stopCall = useCallback(async () => {
      if (callObjectRef.current) {
        const state = callObjectRef.current.meetingState();

        if (state === "joined-meeting") {
          try {
            await callObjectRef.current.stopRecording();
          } catch (e) {
          }
        }

        try {
          await callObjectRef.current.leave();
        } catch (e) {
        }

        callObjectRef.current.destroy();
        callObjectRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        updateLocalStream(null);
      }
    }, [updateLocalStream]);

    useEffect(() => {
      getMedia();
      return () => { void stopCall(); };
    }, []);

    const handleRestartConfirm = async () => {
      setShowRestartSlider(false);
      setRecordingReady(false);
      await stopCall();
      setCountdown(null);
      setTimerReset(prev => prev + 1);
      await getMedia();
    };

    const showOverlay = !recordingReady || countdown !== null;

    return (
      <div className={styles.callContainer}>
        <div className={styles.participants}>
          <div className={styles.participantContainer}>
          {!isAudioOnly && (
                <div className={styles.videoContainer}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={styles.videoElement}
                />
                {callReady && showOverlay && (
                  <>
                   {countdown === null && !recordingReady && (
                      <div className={styles.loadingOverlay}>
                        <span className={styles.loadingText}>preparing</span>
                        <img
                          src="/images/coaching/loader.gif"
                          alt="loading"
                          className={styles.loader}
                        />
                      </div>
                    )}
                    {countdown !== null && (
                      <div key={countdown} className={styles.countdownOverlay}>
                        <span className={styles.number}>{countdown}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {isAudioOnly && (
              <div className={cx(
                styles.videoContainer,
                isAudioOnly && styles.audioOnly
              )}>
                {showOverlay && (
                  <>
                    {countdown === null && !recordingReady && (
                      <div className={styles.loadingOverlay}>
                        <span className={styles.loadingText}>preparing</span>
                        <img
                          src="/images/coaching/loader.gif"
                          alt="loading"
                          className={styles.loader}
                        />
                      </div>
                    )}
                    {countdown !== null && (
                      <div key={countdown} className={styles.countdownOverlay}>
                        <span className={styles.number}>{countdown}</span>
                      </div>
                    )}
                  </>
                )}
                {!showOverlay && userAvatar?.length > 0 && (
                  <img src={userAvatar} alt="Profile" className={styles.profilePic} />
                )}
                {!showOverlay && !userAvatar?.length && (
                  <div className={styles.initialsCircle}>{initials}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles.callMenuBar}>
          <Actions.Row>
            <Actions.Item className={cx(styles.menuItem, styles.timer)}>
              <Timer
                key={timerReset}
                duration={simulation.simulation!.timeBound!}
                onFinish={async () => {
                  await stopCall();   
                  onTimeEnd();
                }}
                disabled={!started}
              />
            </Actions.Item>

            <Actions.IconButton
              className={styles.menuItem}
              icon="paperflite-info"
              onClick={onDetailsClicked}
              tooltip="Call information"
            />

            <Actions.IconButton
              className={styles.menuItem}
              icon="paperflite-reload"
              onClick={() => setShowRestartSlider(true)}
              tooltip="Restart call"
            />

            <Actions.Button
              className={cx(styles.menuItem, styles.endButton)}
              variant="coaching-primary"
              icon="fa-solid fa-phone"
              title="end"
              onClick={onEndSimulation}
              tooltip="End call"
            />
          </Actions.Row>
        </div>

        <RestartPitchSlider
          simulation={simulation}
          show={showRestartSlider}
          onClose={() => setShowRestartSlider(false)}
          onConfirm={handleRestartConfirm}
        />
      </div>
    );
  };

interface TimerProps {
  duration: number;
  disabled: boolean;
  onFinish?: () => void;
}

const Timer: React.FC<TimerProps> = ({ duration, disabled, onFinish }) => {
  const [countDown, setCountDown] = useState(duration);

  useEffect(() => {
    let interval: any = null;
    if (!disabled) {
      interval = setInterval(() => {
        setCountDown(time => Math.max(time - 1, 0));
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    };
  }, [disabled]);

  useEffect(() => {
    if (disabled) {
      setCountDown(duration);
    }
  }, [disabled, duration]);

  useEffect(() => {
    if (countDown <= 0) {
      if (onFinish) {
        onFinish();
      }
    }
  }, [countDown]);

  const display = useMemo(() => {
    const seconds = Math.floor((countDown) % 60);
    const minutes = Math.floor((countDown / (60)) % 60);
    return `${_.padStart(String(minutes), 2, '0')}:${_.padStart(String(seconds), 2, '0')}`;
  }, [countDown]);

  return (
    <div className={styles.timerValue}>
      {display}
    </div>
  );
}

interface RestartPitchSliderProps {
  simulation: LearningRecordSimulation;
  show: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const RestartPitchSlider: React.FC<RestartPitchSliderProps> = ({
  simulation,
  show,
  onConfirm,
  onClose,
}) => {
  return (
    <Slider.Dialog
      className={styles.endDialog}
      show={show}
      width={"23.75rem"}
      onClose={onClose}
    >
      <Slider.Header
        title="restart pitch"
        message={simulation?.simulation?.name}
      />
      <Slider.Body>
        <span className={styles.endCallMessage}>
        Your current recording will be discarded and the timer will reset.
        </span>
      </Slider.Body>
      <Slider.Actions>
        <Button
          className={styles.confirmButton}
          variant="primary"
          icon="paperflite-tick"
          text="Confirm"
          onClick={onConfirm}
        />
        <Button variant="secondary" text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface EndPitchDialogProps {
  show: boolean;
  simulation: LearningRecordSimulation;
  onFinish: () => void;
  onClose: () => void;
}

const EndPitchDialog: React.FC<EndPitchDialogProps> = ({ show, simulation, onFinish, onClose }) => {
  return (
    <Slider.Dialog show={show} width="380px" onClose={onClose} className={styles.completeCallDialog}>
      <Slider.Header title="ending pitch" message={simulation?.simulation?.name}/>
      <Slider.Body>
        <div className={styles.completeCallContainer}>
          <p>Ending now will submit your pitch for assessment.
          If you’d like, you can restart and record again.</p>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button className={styles.actionButton} variant="primary" icon="paperflite-tick" text="Confirm" onClick={onFinish} />
        <Button className={styles.actionButton} variant="secondary" text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  )
}

interface SimulationDetailsProps {
  simulation: LearningRecordSimulation;
  onClose: () => void;
}

const SimulationDetails: React.FC<SimulationDetailsProps> = ({ simulation, onClose }) => {

  const callType = useMemo(() => {
    const value = _.get(simulation, "simulation.type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  return (
    <div className={styles.simulationDetailsContainer}>
      <header>
        <h4>Call Info</h4>
        <button className={styles.closeButton} onClick={onClose}>
          <i className="paperflite-right-arrow" />
        </button>
      </header>
      <div className={cx(styles.callInfo)}>
        <div className={styles.name}>{simulation.simulation.name || ""}</div>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span>{FileService.formatTimeBound(simulation?.simulation?.timeBound ?? 0, 'long')}</span>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <i className="paperflite-calendar"></i>
            <span>{callType}</span>
          </div>
        </div>
      </div>
      <div className={styles.property}>
        <h6 className={styles.name}>Pitch Scenario</h6>
        {simulation.simulation?.pitchScenario &&
          <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.description}>
                {simulation.simulation?.pitchScenario || ""}
              </div>
            </div>
          </div>
        }
        {!simulation.simulation?.pitchScenario &&
          <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.description}>
                No information
              </div>
            </div>
          </div>
        }
      </div>
      <div className={styles.property}>
        <h6 className={styles.name}>Company Audience</h6>
        <div className={styles.body}>
          <div className={styles.persona}>
            <div className={styles.name}>
              {simulation.simulation?.persona?.name || ""}
            </div>
            <div className={styles.description}>
              {simulation.simulation?.pitchAudience || ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CompletedCallViewProps {
  simulation: LearningRecordSimulation;
  onFeedback: () => void;
  onExit: () => void;
}

const CompletedCallView: React.FC<CompletedCallViewProps> = ({ simulation, onFeedback, onExit }) => {
  const simulationContext = React.useContext(SimulationContext);
  const [animate, setAnimate] = React.useState(false);

  const duration = useMemo(() => {
    const raw = simulation.duration || 0;
    const timeBound = simulation?.simulation?.timeBound || 0;
    const value = raw >= timeBound
      ? Math.round(raw / 30) * 30  
      : raw;
    return FileService.formatTimeBound(value, "long");
  }, [simulation]);

  const evaluationStatus = useMemo(() => {
    return simulation.report?.status || "";
  }, [simulation]);

  const personaRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (personaRef.current && userRef.current && !animate) {
      setTimeout(() => {
        setAnimate(true);
      }, 1000);
    }
  }, [animate, personaRef.current, userRef.current]);

  return (
    <div className={cx(styles.callContainer, styles.completed)}>
      <div className={styles.participants}>
      </div>
      <div className={styles.details}>
        <h4 className={styles.message}>Your pitch has been submitted!</h4>
        <div className={styles.duration}>duration: {duration}</div>
        <div className={styles.actions}>
          {(simulation.report?.status === "completed" ||
            (simulation.report?.status === "failed" && simulation.report?.errorCode === 100002)) &&
            <Button className={styles.actionButton} variant="primary"
              icon="paperflite-right-arrow"
              text={simulationContext.fetchingReport ? "Fetching feedback..." : "View feedback"}
              disabled={simulationContext.fetchingReport}
              onClick={onFeedback}
            />
          }
          {simulation.report?.status !== "completed" &&
            !(simulation.report?.status === "failed" && simulation.report?.errorCode === 100002) &&
            <Button className={styles.actionButton} variant="primary"
              icon="paperflite-analytics"
              text={"Processing feedback..."}
            />
          }
          <Button className={styles.actionButton}
            variant="secondary"
            icon="paperflite-close"
            disabled={simulationContext.fetchingReport}
            text="Exit" onClick={onExit}
          />
        </div>
      </div>
    </div>
  )
}

interface FailedCallViewProps {
  simulation: LearningRecordSimulation;
  report: SimulationAssessmentReport;
  onRestart: () => void;
  onExit: () => void;
}

const FailedCallView: React.FC<FailedCallViewProps> = ({ simulation, report, onRestart, onExit }) => {
  const simulationContext = React.useContext(SimulationContext);

  return (
    <div className={cx(styles.failedCallContainer)}>
      <div className={styles.wrapper}>
        <img className={styles.icon} src="/images/coaching/hour_glass.svg" alt="Failed" />
        <h4 className={styles.message}>You need at-least 15+ seconds of call time to get score and feedback analysis</h4>
        <div className={styles.details}>
          <div className={styles.metadata}>
            <div className={styles.label}>
              <i className="paperflite-calendar"></i>
              Call Logged
            </div>
            <div className={styles.value}>
              {dateFormat(report?.startedDate, 'dd mmm yyyy hh:MMTT')}
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.metadata}>
            <div className={styles.label}>
              <i className="paperflite-clock"></i>
              Duration
            </div>
            <div className={styles.value}>{FileService.formatDuration(report?.duration, 'long')}</div>
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        <Button className={styles.actionButton} variant="primary"
          icon="paperflite-reverse"
          text={simulationContext.restartingSimulation ? "Restarting..." : "Try again"}
          disabled={simulationContext.restartingSimulation}
          onClick={onRestart}
        />
        <Button className={styles.actionButton}
          variant="secondary"
          icon="paperflite-close"
          disabled={simulationContext.restartingSimulation}
          text="Exit" onClick={onExit}
        />
      </div>
    </div>
  )
}

interface EndPitchConfirmationDialogProps {
  show: boolean;
  simulation: LearningRecordSimulation;
  onFinish: () => void;
  onClose: () => void;
}

const EndPitchConfirmationDialog: React.FC<EndPitchConfirmationDialogProps> = ({ show, simulation, onFinish, onClose }) => {
  return (
    <Slider.Dialog show={show} width="380px" onClose={onClose} className={styles.completeCallDialog}>
      <Slider.Header title="ending pitch" message={simulation?.simulation?.name} />
      <Slider.Body>
        <div className={styles.completeCallContainer}>
          <p>You will be redirected to your assessment of the pitch if you choose to exit. Else, you can restart and submit.</p>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button className={styles.actionButton} variant="primary" icon="paperflite-tick" text="Exit" onClick={onFinish} />
        <Button className={styles.actionButton} variant="secondary" text="Restart" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  )
}

interface CallAssessmentViewProps {
  simulation: LearningRecordSimulation;
  report: SimulationAssessmentReport;
}

const CallAssessmentView: React.FC<CallAssessmentViewProps> = ({ simulation, report }) => {
  const simulationContext = React.useContext(SimulationContext);

  const playerMinimized = simulationContext.secondaryView === 'call-recording-minimized';
  const showAssessmentView = simulationContext.secondaryView === null || playerMinimized;
  const showCallRecordingView = simulationContext.secondaryView === 'call-recording' || playerMinimized;
  const transcriptEmpty = report?.status === "failed" && report?.errorCode === 100002;

  const duration = useMemo(() => {
    if (simulation?.simulation?.type === "elevator_pitch") {
      const raw = simulation.duration || 0;
      const timeBound = simulation?.simulation?.timeBound || 0;
      const value = raw >= timeBound
        ? Math.round(raw / 30) * 30  
        : raw;
      return FileService.formatTimeBound(value, "long");
    }
    return FileService.formatDuration(report?.duration, "long");
  }, [simulation, report]);

  const assessmentTransition = useTransition(showAssessmentView, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const callRecordingTransition = useTransition(showCallRecordingView, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  return (
    <DndContext sensors={sensors} modifiers={[restrictToWindowEdges]}>
      <div className={cx(styles.callAssessmentContainer, {
        [styles.open]: showAssessmentView || showCallRecordingView,
      })}>
        {assessmentTransition((style, item) => (
          item && <animated.div style={style} className={cx(styles.stack, {
            [styles.minimized]: playerMinimized,
          })}>
            <div className={styles.callAssessment}>
              {transcriptEmpty ? (
                <>
                <div className={styles.summaryContainer}>
                  <img className={styles.icon} src="/images/coaching/hour_glass.svg" alt="No speech detected" />
                  <h4 className={styles.message}>
                    Deliver your pitch for at least 15 seconds to receive your score and feedback
                  </h4>
                  <div className={styles.details}>
                    <div className={styles.metadata}>
                      <div className={styles.label}>
                        <i className="paperflite-calendar"></i>
                        Call Logged
                      </div>
                      <div className={styles.value}>
                        {dateFormat(report?.startedDate, 'dd mmm yyyy hh:MMTT')}
                      </div>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.metadata}>
                      <div className={styles.label}>
                        <i className="paperflite-clock"></i>
                        Duration
                      </div>
                      <div className={styles.value}>{duration}</div>
                    </div>
                  </div>
                </div>
                <div className={styles.transcriptEmptyPlaceholders}>
                  <div className={styles.transcriptEmptyParameterRow}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div key={index} className={styles.transcriptEmptyParameter} />
                    ))}
                  </div>
                  <div className={styles.transcriptEmptyContent} />
                </div>
                </>
              ) : (
                <>
              <div className={styles.summaryContainer}>
                <img className={styles.icon} src="/images/coaching/trophy.svg" alt="Congratulations" />
                <h4 className={styles.message}>Congratulations! <br /> your overall score is <b>{(2 * report.score).toFixed(1)}</b>/10</h4>
                <div className={styles.details}>
                  <div className={styles.metadata}>
                    <div className={styles.label}>
                      <i className="paperflite-calendar"></i>
                      Call Logged
                    </div>
                    <div className={styles.value}>
                      {dateFormat(report?.startedDate, 'dd mmm yyyy hh:MMTT')}
                    </div>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.metadata}>
                    <div className={styles.label}>
                      <i className="paperflite-clock"></i>
                      Duration
                    </div>
                    <div className={styles.value}>{duration}</div>
                  </div>
                  {simulation?.simulation?.type !== "elevator_pitch" && (
                    <>
                      <div className={styles.divider} />
                      <div className={cx(styles.metadata, styles.persona)}>
                        <div className={styles.label}>
                          <i className="paperflite-user-settings"></i>
                          Prospect
                        </div>
                        <div className={styles.value}>
                          <Thumbnail className={styles.icon} media={null} defaultImage="/images/assets/default_asset.jpg" />
                          {simulation.simulation?.persona?.name || ""}, {simulation.simulation?.persona?.designation || ""}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.tabContainer}>
                <Tab.Container defaultActiveKey="0">
                  <Nav className={styles.parametersContainer}>
                    {(report.parameters || []).map((parameter, index) => {
                      return (
                        <Nav.Link key={parameter.uuid} className={styles.parameter} eventKey={index}>
                          <div className={styles.name}>{`${parameter.name}`}</div>
                          <div className={styles.score}>{`${parseFloat((parameter.score).toFixed(2))}`}</div>
                        </Nav.Link>
                      )
                    })}
                         {_.size(report?.factChecks) > 0 && (
                      <Nav.Link
                        className={cx(styles.parameter, styles.factCheckerTab)}
                        eventKey="fact-checker"
                      >
                        <div className={styles.name}>Claim Validation</div>
                        <div className={styles.factChecker}>
                          <span className={styles.count}>
                            {_.size(report.factChecks)}
                          </span>
                          <span className={styles.label}>incorrect claims</span>
                          <i className={cx("fa-solid fa-triangle-exclamation", styles.icon)} />
                        </div>
                      </Nav.Link>
                    )}
                  </Nav>

                  <Tab.Content className={styles.tabContent}>
                    {(report.parameters || []).map((parameter, index) => {
                      return (
                        <Tab.Pane eventKey={index} key={parameter.uuid}>
                          <CallAssessmentParameterView simulation={simulation} parameter={parameter} />
                        </Tab.Pane>
                      )
                    })}
                      {_.size(report?.factChecks) > 0 && (
                      <Tab.Pane eventKey="fact-checker">
                        <CallAssessmentFactInsights
                          simulation={simulation}
                          factChecks={report.factChecks}
                        />
                      </Tab.Pane>
                    )}
                  </Tab.Content>
                </Tab.Container>
              </div>
                </>
              )}
            </div>
          </animated.div>
        ))}

        {callRecordingTransition((style, item) => {
          const { transform, ...rest } = style;
          return (
            item && <animated.div style={{
              ...rest,
              transform: transform.to((t) => (t ? `translateX(${t}%)` : 'none')),
            }} className={cx(styles.stack, {
              [styles.minimized]: playerMinimized,
            })}>
              <CallRecordingView simulation={simulation} minimized={playerMinimized} />
            </animated.div>
          )
        })}
      </div>
    </DndContext>
  )
}

interface CallAssessmentParameterViewProps {
  simulation: LearningRecordSimulation;
  parameter: SimulationParameterEvaluation;
}

const CallAssessmentParameterView: React.FC<CallAssessmentParameterViewProps> = ({ simulation, parameter }) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>
      <p className={styles.message}>here&apos;s the list of claims validated</p>
      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">
          <Nav className={styles.tabs}>
            {parameter.parameters.map((parameter, index) => {
              return (
                <Nav.Link key={parameter.uuid} className={styles.tab} eventKey={index}>
                  <span>{parameter.name} ({parameter.score})</span>
                </Nav.Link>
              )
            })}
          </Nav>
          <Tab.Content className={styles.tabContent}>
            {parameter.parameters.map((subParameter, index) => {
              const showEmptyMessage =
                _.isEmpty(subParameter.scenarios) &&
                _.isEmpty(subParameter.positives) &&
                _.isEmpty(subParameter.suggestions) &&
                _.isEmpty(subParameter.missedOpportunities) &&
                (!subParameter.score || subParameter.score === 0);

              if (showEmptyMessage) {
                return (
                  <Tab.Pane
                    className={styles.parameterContainer}
                    eventKey={index}
                    key={subParameter.uuid}
                  >
                    <div className={styles.noData}>
                      <div className={styles.text}>No scenario found</div>
                      <span className={styles.subText}>
                        There were no relevant scenarios in the call to evaluate
                        this parameter.
                      </span>
                    </div>
                  </Tab.Pane>
                );
              }

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={subParameter.uuid}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Description</div>
                    <div className={styles.value}>
                      {subParameter.description}
                    </div>
                  </div>
                  <div className={styles.parameter}>
                    <div className={styles.label}>Score</div>
                    <div className={styles.value}>{subParameter.score}</div>
                  </div>
                  {_.size(subParameter.scenarios) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Scenario</div>
                      <div className={styles.value}>
                        <ul className={styles.content}>
                          {_.map(subParameter.scenarios, (scenario, index) => (
                            <li key={index}>
                              <p>{scenario.description}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {subParameter.positives && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Positives</div>
                      <div className={styles.value}>
                        {subParameter.positives}
                      </div>
                    </div>
                  )}

                  <div className={styles.parameter}>
                    <div className={styles.label}>Suggestions</div>
                    <div className={styles.value}>
                      {subParameter.suggestions}
                    </div>
                  </div>

                  {_.size(subParameter.missedOpportunities) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Missed opportunities</div>
                      <div className={styles.value}>
                        <ul className={styles.content}>
                          {_.map(
                            subParameter.missedOpportunities,
                            (item, index) => (
                              <li key={index}>
                                <p className={styles.example}>{item}</p>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

interface CallRecordingViewProps {
  simulation: LearningRecordSimulation;
  minimized: boolean;
}

const CallRecordingView: React.FC<CallRecordingViewProps> = ({ simulation, minimized }) => {
  const simulationContext = React.useContext(SimulationContext);
  const [position, setPosition] = useState({ top: 0, left: 0, bottom: 0, right: 20 });

  const { attributes, listeners, setNodeRef, transform, node } = useDraggable({
    id: 'call-recording-player',
    disabled: !minimized,
  });

  useDndMonitor({
    onDragEnd(event) {
      const { delta } = event;
      if (delta) {

        setPosition((prev) => {
          const top = Math.max(prev.top + delta.y, 0);
          const left = Math.max(prev.left + delta.x, 0);

          return {
            ...prev,
            top: top,
            left: left,
          }
        });
      }
    },
  });

  const positionStyle = minimized ? {
    top: position.top,
    left: position.left,
    transform: CSS.Transform.toString(transform) || undefined,
  } : undefined;

  useEffect(() => {
    if (simulation.id) {
      simulationContext.fetchTranscripts?.();
    }
  }, [simulation.id]);

  useEffect(() => {
    if (minimized && node.current) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const bodyElement = document.querySelector(`.${styles.body}`);
      const bodyWidth = bodyElement?.clientWidth || viewportWidth;
      const bodyTop = bodyElement?.getBoundingClientRect().top || 0;

      const nodeWidth = node.current.offsetWidth || 400; // Default width if not available
      const nodeHeight = node.current.offsetHeight || 300; // Default height if not available

      console.log("Node dimensions:", nodeWidth, nodeHeight);
      console.log("Viewport dimensions:", viewportWidth, viewportHeight);
      console.log("Body dimensions:", bodyWidth, bodyTop);

      setPosition({
        top: viewportHeight - nodeHeight - 40, // 20px from the bottom
        left: viewportWidth - nodeWidth - 40, // 20px from the right
        bottom: 0,
        right: 20
      });
    }
  }, [minimized]);

  return (
    <div className={cx(styles.callContainer, styles.callRecording, {
      [styles.minimized]: minimized,
    })} ref={setNodeRef} style={positionStyle} {...listeners} {...attributes}>
      <div className={styles.left}>
        <div className={styles.participants}>
          {(simulationContext.secondaryView === 'call-recording' || simulationContext.secondaryView === 'call-recording-minimized') && (
            <CallRecordingPlayer
              simulation={simulation}
              minimized={simulationContext.secondaryView === 'call-recording-minimized'}
            />
          )}
        </div>
        <div className={styles.callRecording}>
        </div>
      </div>
      <div className={styles.right}>
        <Transcripts simulation={simulation} />
      </div>
    </div>
  )
}

interface CallTranscriptsProps {
  simulation: LearningRecordSimulation;
}

export const Transcripts: React.FC<CallTranscriptsProps> = ({ simulation }) => {
  const simulationContext = React.useContext(SimulationContext);
  const transcripts = simulationContext.transcripts || [];
  const fetchingTranscripts = simulationContext.fetchingTranscripts || false;

  if (fetchingTranscripts && _.size(transcripts) === 0) {
    return (
      <div className={styles.transcripts}>
        {_.times(4).map((_, index) => (
          <TranscriptRowPlaceholder key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.transcripts}>
      {(transcripts || []).filter(t => t.message).map((transcript, index) => (
        <p key={index} className={styles.transcriptMessage}>
          {transcript.message}
        </p>
      ))}
    </div>
  );
};

const TranscriptRowPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.transcriptRow, styles.placeholder)}>
      <div className={styles.speaker}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={cx(styles.text)}>
        <div className={cx(styles.block)}></div>
        <div className={cx(styles.block)}></div>
        <div className={cx(styles.block)}></div>
      </div>
    </div>
  );
}
interface CallAssessmentFactInsightsProps {
  simulation: LearningRecordSimulation;
  factChecks: FactCheckEvaluation[];
}

const CallAssessmentFactInsights: React.FC<CallAssessmentFactInsightsProps> = ({
  simulation,
  factChecks
}) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>
      <p className={styles.message}>
        here&apos;s the list of claims evaluated for you,
      </p>

      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">

          <Nav className={styles.tabs}>
            {factChecks.map((item, index) => (
              <Nav.Link
                key={index}
                className={styles.tab}
                eventKey={index}
              >
                <span>Claim {index + 1}</span>
              </Nav.Link>
            ))}
          </Nav>

          <Tab.Content className={styles.tabContent}>
            {factChecks.map((item, index) => {

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={index}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Rep Statement <span className={styles.timestamp}>{item.timestamp}</span></div>
                    <div className={styles.value}>
                      {item?.claim}
                    </div>
                  </div>

                  {item?.feedback && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Knowledge Reference</div>
                      <div className={styles.value}>
                        {item?.feedback}
                      </div>
                    </div>
                  )}

                  {/* {evidence?.answer && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Verified answer</div>

                      <div className={styles.value}>
                        {evidence.answer}
                      </div>

                      {evidence.citations?.length > 0 && (
                        <div className={styles.citations}>
                          {evidence.citations.map((citation, i) => (
                            <div key={i} className={styles.citation}>
                              <span className={styles.citationIndex}>
                                {i + 1}
                              </span>
                              <span className={styles.citationText}>
                                {citation}
                              </span>
                              <i
                                className={cx(
                                  "paperflite-arrow-up",
                                  styles.citationArrow
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )} */}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};